import {InjectionToken, computed, inject, signal} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';

import {filter, firstValueFrom} from 'rxjs';

import {ConfirmationDialogCheckbox} from 'frontend/components/confirmation_dialog_checkbox/confirmation_dialog_checkbox';
import {Override} from 'frontend/platform/generics';

import {Bucket} from '../config_types';
import {GcpIapIdentity, IdentityService} from '../identity_service';
import {LaunchDarklyService} from '../launch_darkly_service';
import type {UserScopeService} from '../user_scope_service';
import {FeedbackAndAnalyticsResolver} from './feedback_and_analytics_resolver';
import {UserFeedback} from './types';

export interface FeedbackOptions {
  showPrivacyDisclaimer?: boolean;
}

/**
 * A fallback constant for when the user's organization cannot be determined.
 */
export const UNKNOWN_ORGANIZATION = 'unknown';

/* eslint-disable @typescript-eslint/no-explicit-any */

declare const userfeedback: UserFeedback;

/**
 * Feedback And Analytics Service
 *
 * Our behavior for detecting whether or not to enable feedback
 * and google analytics are identical - but prior to this were
 * written and resolved through several outlets - combination
 * of build level arguments, protos, and network requests.
 *
 * This service simplifies the resolution into a single-source
 * setup where both workflows can live along side each other
 * concisely.
 *
 * Of course these feedback and analytics parameters differ
 * between apps - so to get setup with this service, simply
 * extend it and consume it using the `FEEDBACK_AND_ANALYTICS_SERVICE_TOKEN`
 *
 * ```typescript
 * @Injectable({providedIn: 'root'})
 * export class YourAppFeedbackAndAnalyticsService extends FeedbackAndAnalyticsService {
 *    override userScope = inject(UserScopeService);
 *    override resolver = new FeedbackAndAnalyticsResolver({
 *      feedbackProductIDFlag: 'your-app-product-feedback-id',
 *      feedbackBucketFlag: 'your-app-feedback-bucket-id',
 *      trackingIDFlag: 'your-app-google-analytics-tracking-id',
 *      analyticsEnabledFlag: 'your-app-frontend-google-analytics-enabled',
 *   });
 *   constructor() {
 *    super();
 *   }
 * }
 * ```
 *
 * 10 lines of code and you're done!
 */
export abstract class FeedbackAndAnalyticsService {
  readonly feedbackEnabled = signal(false);
  readonly userOrganization = signal(UNKNOWN_ORGANIZATION);
  abstract readonly userScope: UserScopeService;
  abstract readonly resolver: FeedbackAndAnalyticsResolver;
  protected readonly identityService = inject(IdentityService);
  protected readonly launchDarklyService = inject(LaunchDarklyService);
  readonly dialog = inject(MatDialog);
  readonly analyticsEnabled = computed(() => this.resolver.analyticsEnabledForCurrentUser());

  constructor(public readonly disableForInternalUsers = true) {
    this.launchDarklyService.ready().then(() => {
      this.onInitialized();
    });
  }

  public onInitialized(identityInitializer = () => this.identityService.fetchGcpIapIdentity()) {
    this.resolver.initialize();
    this.feedbackEnabled.set(this.resolver.isFeedbackEnabledForCurrentUser);
    if (this.disableForInternalUsers) {
      this.resolver.disableTrackingIfInternal(this.userScope.getIsInternalUser());
    }
    this.initializeUserContext(identityInitializer);
  }

  /**
   * Begins the feedback flow, launching a dialog where the user can highlight
   * part of the current UI and leave feedback in freetext form.
   */
  async start(options: FeedbackOptions = {showPrivacyDisclaimer: false}) {
    if (!this.resolver.feedbackProductID) {
      return;
    }
    if (!userfeedback) {
      // eslint-disable-next-line no-console
      return console.error('Feedback service failed to load');
    }

    let confirmed = true;

    // Only open the Gatekeeper if the flag is explicitly passed
    if (options.showPrivacyDisclaimer) {
      const message =
        'By submitting, you confirm this feedback contains no third-party proprietary data, trade secrets, or restricted materials.';
      const checkboxLabel = 'I agree and confirm I have the right to share this information.';
      const dialogRef = this.dialog.open(ConfirmationDialogCheckbox, {
        width: '450px',
        data: {message, checkboxLabel},
      });
      confirmed = await firstValueFrom(dialogRef.afterClosed());
    }

    // Only trigger the widget if the dialog returns true
    if (confirmed) {
      userfeedback.api.startFeedback({
        disableScreenshotting: this.resolver.disableScreenshotting,
        productId: this.resolver.feedbackProductID,
        bucket: this.resolver.feedbackBucket as Bucket,
        enableAnonymousFeedback: this.resolver.enableAnonymousFeedback,
        productSpecificData: {
          organization: this.userOrganization(),
        },
      });
    }
  }

  /**
   * Send Event
   *
   * Sends google analytics events routed to the
   * application in scope
   */
  public sendEvent = this.withApplicationScope(
    (
      eventName: string,
      params?: Override<Gtag.EventParams, {value?: string | number}> & Record<string, any>,
    ) => {
      gtag('event', eventName, {...params, send_to: this.resolver.trackingID});
    },
  );

  /**
   * Send Page View
   *
   * Sends google analytics page view events routed to the
   * application in scope
   */
  public sendPageView = this.withApplicationScope((params?: Gtag.ConfigParams) => {
    // Use the 'config' command to trigger a page_view for an SPA.
    gtag('config', this.resolver.trackingID, this.toRoutedParams(params));
  });

  /**
   * Set Property
   *
   * Sets a user property routed to the application in scope
   */
  public setProperty = this.withApplicationScope((property: string, params: Gtag.CustomParams) => {
    gtag('set', property, this.toRoutedParams(params));
  });

  /**
   * Set Context
   *
   * Sets user context routed to the application in scope
   */
  public setContext = this.withApplicationScope((params: Gtag.CustomParams) => {
    gtag('set', this.toRoutedParams(params));
  });

  /**
   * With Application Scope
   *
   * Turns calls to gtag into noops when analytics is disabled with
   * no if else checked required in your app code.
   */
  private withApplicationScope<F extends (...args: any[]) => any>(operation: F) {
    return (...args: Parameters<F>): undefined | ReturnType<F> => {
      if (!this.analyticsEnabled()) {
        return;
      }
      return operation(...args);
    };
  }

  /**
   * Represents the identity information provided by GCP's Identity-Aware Proxy (IAP).
   * This data is typically retrieved from a special endpoint (`?gcp-iap-mode=IDENTITY`)
   * when IAP is enabled.
   *
   * @see https://cloud.google.com/iap/docs/identity-howto
   */

  /**
   * Parses the organization name from the GCP IAP identity response email.
   *
   * This function assumes the organization name is the substring strictly between
   * the '@' and the first '.' in the domain.
   *
   * Examples:
   * - "google" is parsed from user@google.com
   * - "corp" is parsed from user@corp.google.com
   *
   * Supports both IAP-prefixed formats (e.g., accounts.google.com:user@org.com)
   * and standard email formats (e.g., user@org.com).
   *
   * @param identityResponse - The GCP IAP identity object.
   * @returns The organization name in lowercase, or UNKNOWN_ORGANIZATION.
   */
  public parseOrganizationFromIdentity(identityResponse: GcpIapIdentity | null): string {
    const email = identityResponse?.email;
    if (!email) {
      return UNKNOWN_ORGANIZATION;
    }
    // Remove potential IAP prefix by taking everything after the last ':'.
    const emailClean = email.substring(email.lastIndexOf(':') + 1);
    // Extract the part between '@' and the first '.'.
    const match = emailClean.match(/@([^.]+)/);

    return match?.[1]?.toLowerCase() ?? UNKNOWN_ORGANIZATION;
  }

  /**
   * Initializes Google Analytics organization tracking by setting user properties.
   * It first sets a default value and then asynchronously fetches the GCP IAP identity
   * to determine and set the actual organization name.
   *
   * @param source
   * @param trackingID - An optional to override the tracking ID for the service in scope
   */
  public initializeUserContext(
    source = () => this.identityService.fetchGcpIapIdentity(),
    trackingID = this.resolver.trackingID,
  ) {
    this.setProperty('user_properties', {
      organization_name: UNKNOWN_ORGANIZATION,
      send_to: trackingID,
    });
    source()
      .pipe(filter(Boolean))
      .subscribe({
        next: (identity: GcpIapIdentity) => {
          const userId = identity.sub;
          if (userId) {
            this.setContext({user_id: userId, send_to: trackingID});
          }
          const org = this.parseOrganizationFromIdentity(identity);
          this.userOrganization.set(org);
          this.setProperty('user_properties', {organization_name: org, send_to: trackingID});
        },
      });
  }

  private toRoutedParams<T extends Record<string, any> | undefined>(params: T) {
    if (!params || !('send_to' in params)) {
      return params;
    }
    return {...params, send_to: this.resolver.trackingID};
  }
}

/**
 * InjectionToken used to inject the application's feedback service.
 * Shared components and services should depend on this token to obtain an
 * instance of the provided `FeedbackAndAnalyticsService` implementation instead of
 * direct service injection.
 */
export const FEEDBACK_AND_ANALYTICS_SERVICE_TOKEN = new InjectionToken<FeedbackAndAnalyticsService>(
  'FEEDBACK_AND_ANALYTICS_SERVICE_TOKEN',
);
